import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { motion as Motion } from "framer-motion";

import api from "@/utils/axios";
import i18n from "@/i18n";

import cls from "./WalletPromoRedeem.module.scss";

import coinIcon from "@/assets/UI/coin.webp";
import mrImg from "@/assets/UI/memberCreate/q20Mr.webp";
import mrMImg from "@/assets/UI/memberCreate/q20MrM.webp";
import mrsImg from "@/assets/UI/memberCreate/q20Mrs.webp";
import mrsMImg from "@/assets/UI/memberCreate/q20MrsM.webp";

const floatSlow = {
  hidden: { opacity: 0.55 },
  show: {
    opacity: [0.55, 0.72, 0.55],
    scale: [1, 1.03, 1],
    transition: { duration: 10, ease: "easeInOut", repeat: Infinity },
  },
};

const floatSlowAlt = {
  hidden: { opacity: 0.55 },
  show: {
    opacity: [0.72, 0.58, 0.72],
    scale: [1.03, 1, 1.03],
    x: [0, -6, 0],
    transition: { duration: 12, ease: "easeInOut", repeat: Infinity },
  },
};

const formatNoty = (value) =>
  Number(value || 0).toLocaleString(undefined, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });

export default function WalletPromoRedeem() {
  const MrD = useMemo(() => mrImg, []);
  const MrsD = useMemo(() => mrsImg, []);
  const MrM = useMemo(() => mrMImg, []);
  const MrsM = useMemo(() => mrsMImg, []);

  const t = (key, fallback) => i18n.t(key, { defaultValue: fallback });

  const [code, setCode] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [status, setStatus] = useState(null);

  const handleSubmit = async (event) => {
    event.preventDefault();
    const trimmed = code.trim();

    if (!trimmed) {
      setStatus({
        type: "error",
        message: t(
          "common.wallet.promoRedeem.empty",
          "Please enter a promo code.",
        ),
      });
      return;
    }

    try {
      setSubmitting(true);
      setStatus(null);
      const { data } = await api.post(
        "/v1/promos/redeem",
        { code: trimmed },
        { skipToast: true },
      );

      const amountNoty = data?.amountNoty ?? 0;
      const balanceNoty = data?.balanceNoty ?? null;

      const successMessage =
        balanceNoty !== null
          ? t(
              "common.wallet.promoRedeem.success",
              `Wallet refilled with ${formatNoty(
                amountNoty,
              )} NOTY. New balance: ${formatNoty(balanceNoty)} NOTY.`,
            )
          : t(
              "common.wallet.promoRedeem.successNoBalance",
              `Wallet refilled with ${formatNoty(amountNoty)} NOTY.`,
            );

      setStatus({ type: "success", message: successMessage });
      setCode("");
    } catch (error) {
      const message =
        error?.response?.data?.message ||
        error?.message ||
        t(
          "common.wallet.promoRedeem.error",
          "We couldn’t redeem that code. Please try again.",
        );

      setStatus({ type: "error", message });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main className={cls.page}>
      <section className={cls.heroStage} aria-hidden="true">
        <div className={cls.mobileOnly}>
          <Motion.img
            className={`${cls.heroImg} ${cls.z1}`}
            src={MrM}
            alt=""
            variants={floatSlow}
            initial="hidden"
            animate="show"
            draggable="false"
          />
          <Motion.img
            className={`${cls.heroImg} ${cls.z3}`}
            src={MrsM}
            alt=""
            variants={floatSlowAlt}
            initial="hidden"
            animate="show"
            draggable="false"
          />
        </div>

        <div className={cls.desktopOnly}>
          <Motion.img
            className={`${cls.heroImg} ${cls.z1}`}
            src={MrD}
            alt=""
            variants={floatSlow}
            initial="hidden"
            animate="show"
            draggable="false"
          />
          <Motion.img
            className={`${cls.heroImg} ${cls.z3}`}
            src={MrsD}
            alt=""
            variants={floatSlowAlt}
            initial="hidden"
            animate="show"
            draggable="false"
          />
        </div>

        <div className={cls.stageTint} />
      </section>

      <div className={cls.pageInner}>
        <div className={cls.contentShell}>
          <div className={cls.promoCard}>
            <header className={cls.cardHeader}>
              <div className={cls.headerText}>
                <p className={cls.kicker}>
                  {t("common.wallet.promoRedeem.kicker", "Member Wallet")}
                </p>
                <h1 className={cls.title}>
                  {t("common.wallet.promoRedeem.title", "Redeem a promo code")}
                </h1>
                <p className={cls.subtitle}>
                  {t(
                    "common.wallet.promoRedeem.subtitle",
                    "Enter your promo code below to refill your Noty balance instantly.",
                  )}
                </p>
              </div>
              <div className={cls.summaryPill}>
                <img
                  className={cls.coin}
                  src={coinIcon}
                  alt={t("common.wallet.coinAlt", "Noty Coin")}
                />
                {t(
                  "common.wallet.promoRedeem.note",
                  "Promo credits apply immediately.",
                )}
              </div>
            </header>

            <form className={cls.form} onSubmit={handleSubmit}>
              <label className={cls.label} htmlFor="promo-code">
                {t("common.wallet.promoRedeem.label", "Promo code")}
              </label>
              <div className={cls.inputRow}>
                <input
                  id="promo-code"
                  className={cls.input}
                  type="text"
                  value={code}
                  onChange={(event) => setCode(event.target.value)}
                  placeholder={t(
                    "common.wallet.promoRedeem.placeholder",
                    "Enter code",
                  )}
                  autoComplete="off"
                  spellCheck="false"
                />
                <button
                  className={cls.primaryButton}
                  type="submit"
                  disabled={submitting}
                >
                  {submitting
                    ? t("common.wallet.promoRedeem.submitting", "Redeeming…")
                    : t("common.wallet.promoRedeem.cta", "Redeem code")}
                </button>
              </div>
              <p className={cls.helper}>
                {t(
                  "common.wallet.promoRedeem.helper",
                  "Promo codes are case-insensitive and can only be used once per member.",
                )}
              </p>

              {status ? (
                <div
                  className={`${cls.statusMessage} ${
                    {
                      success: cls.statusSuccess,
                      error: cls.statusError,
                    }[status.type]
                  }`}
                  role="status"
                  aria-live="polite"
                >
                  {status.message}
                </div>
              ) : null}

              <div className={cls.actions}>
                <Link className={cls.linkButton} to="/member/wallet">
                  {t("common.wallet.promoRedeem.back", "Back to wallet")}
                </Link>
              </div>
            </form>
          </div>
        </div>
      </div>
    </main>
  );
}
